/* global axios */
import ApiClient from './ApiClient';

/**
 * REST client for the support case engine.
 *
 * Cases are addressed by their per-account `display_id` (the number agents
 * quote, e.g. #142), which is what the server routes on — the numeric primary
 * key never appears in a URL. Every mutation maps to exactly one server-side
 * command, so this client stays a translation layer with no logic of its own.
 */
class SupportCasesAPI extends ApiClient {
  constructor() {
    super('support_cases', { accountScoped: true });
  }

  get(params = {}) {
    return axios.get(this.url, { params });
  }

  getCounts(params = {}) {
    return axios.get(`${this.url}/counts`, { params });
  }

  changeStage(displayId, caseStageId) {
    return axios.post(`${this.url}/${displayId}/change_stage`, {
      case_stage_id: caseStageId,
    });
  }

  // Owner is an individual OR a team, exactly one; both null unassigns.
  assign(displayId, { assigneeId = null, assigneeTeamId = null } = {}) {
    return axios.post(`${this.url}/${displayId}/assign`, {
      assignee_id: assigneeId,
      assignee_team_id: assigneeTeamId,
    });
  }

  archive(displayId) {
    return axios.post(`${this.url}/${displayId}/archive`);
  }

  unarchive(displayId) {
    return axios.post(`${this.url}/${displayId}/unarchive`);
  }

  getComments(displayId, params = {}) {
    return axios.get(`${this.url}/${displayId}/comments`, { params });
  }

  createComment(displayId, { content, parentId = null }) {
    return axios.post(`${this.url}/${displayId}/comments`, {
      content,
      parent_id: parentId,
    });
  }

  updateComment(displayId, commentId, { content }) {
    return axios.patch(`${this.url}/${displayId}/comments/${commentId}`, {
      content,
    });
  }

  // Deleting a comment archives it; the thread renders a tombstone.
  deleteComment(displayId, commentId) {
    return axios.delete(`${this.url}/${displayId}/comments/${commentId}`);
  }

  getActivities(displayId, params = {}) {
    return axios.get(`${this.url}/${displayId}/activities`, { params });
  }

  getTasks(displayId) {
    return axios.get(`${this.url}/${displayId}/tasks`);
  }

  createTask(displayId, { title, assigneeId = null }) {
    return axios.post(`${this.url}/${displayId}/tasks`, {
      title,
      assignee_id: assigneeId,
    });
  }

  updateTask(displayId, taskId, payload) {
    return axios.patch(`${this.url}/${displayId}/tasks/${taskId}`, payload);
  }

  deleteTask(displayId, taskId) {
    return axios.delete(`${this.url}/${displayId}/tasks/${taskId}`);
  }

  // `conversationId` is the internal id the read payload reports, not the
  // conversation's display id: the write takes back the value the read handed
  // out.
  createConversationLink(displayId, { conversationId, role = 'related' }) {
    return axios.post(`${this.url}/${displayId}/conversation_links`, {
      conversation_id: conversationId,
      role,
    });
  }

  // Answers with the case, not the link: the row is gone, and the unlink
  // survives in the activity trail.
  deleteConversationLink(displayId, linkId) {
    return axios.delete(
      `${this.url}/${displayId}/conversation_links/${linkId}`
    );
  }
}

export default new SupportCasesAPI();
